import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { BookingDetailDto } from '@opb-workspace/shared';
import { BookingsService } from '../services/bookings.service';
import { BookingsNavigationService } from '../services/bookings-navigation.service';

@Component({
  selector: 'app-booking-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTabsModule,
    MatChipsModule,
    MatDividerModule
  ],
  templateUrl: './booking-detail.component.html',
  styleUrls: ['./booking-detail.component.scss']
})
export class BookingDetailComponent implements OnInit {
  booking: BookingDetailDto | null = null;
  isLoading = true;
  hasError = false;
  errorMessage = '';

  displayedColumnsContainers: string[] = [
    'index',
    'carrierBookingNumber',
    'masterBl',
    'containerNumber'
  ];

  constructor(
    private route: ActivatedRoute,
    private bookingsService: BookingsService,
    private navigationService: BookingsNavigationService
  ) { }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      const id = +idParam;
      this.loadBooking(id);
    } else {
      this.hasError = true;
      this.errorMessage = 'ID de reserva no válido.';
      this.isLoading = false;
    }
  }

  loadBooking(id: number): void {
    this.isLoading = true;
    this.hasError = false;

    this.bookingsService.getDetail(id).subscribe({
      next: (data) => {
        this.booking = data;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading booking detail', err);
        this.errorMessage = 'No se pudo cargar el detalle de la reserva.';
        this.hasError = true;
        this.isLoading = false;
      }
    });
  }

  goBack(): void {
    this.navigationService.backToList();
  }

  formatDate(dateStr?: string | null): string {
    if (!dateStr) return '-';
    // Remove "T" and seconds if needed, but toLocaleDateString is enough for date part.
    // Screenshot shows: DD-MM-YYYY HH:mm or similar.
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';

    // Custom format: DD-MM-YYYY HH:mm
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');

    // If midnight, maybe just date? Screenshot shows 00:00.
    return `${day}-${month}-${year} ${hours}:${mins}`;
  }

  formatVoyageId(id?: string | number): string {
    if (!id) return '-';
    return String(id).padStart(8, '0');
  }

  formatBookingId(id?: number): string {
    if (!id) return '-';
    // Format to 6 digits? Or 8? Let's use 6 as per typical booking IDs or 8 to match voyage.
    // The previous implementation used 6 for itineraries?
    // Let's stick with padding to 6 or 8. Usually IDs are 4-6 digits.
    // I will use 6 for now as "000000" seems reasonable.
    return String(id).padStart(8, '0');
  }

  formatRoute(name?: string, dateStr?: string): string {
    const nameDisplay = name || '-';
    if (!dateStr) return nameDisplay;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return nameDisplay;

    // Format: NAME / DD-MM-YYYY
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${nameDisplay} / ${day}-${month}-${year}`;
  }

  getPackagesSummary(container: any): string {
    if (!container.packages || container.packages.length === 0) return 'Sin Bultos';
    return container.packages.map((p: any) => `${p.quantity} x ${p.packageType} (${p.weight} kg)`).join(', ');
  }

  isDownloadingPdf = false;

  downloadPdf(): void {
    if (!this.booking) return;
    this.isDownloadingPdf = true;

    this.bookingsService.downloadPdf(this.booking.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Reserva-${this.formatBookingId(this.booking?.id)}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPdf = false;
      },
      error: (err) => {
        console.error('Error downloading PDF', err);
        this.isDownloadingPdf = false;
      }
    });
  }
}
